/**
 * Status history stage — structure-scoped, nested in the site dashboard.
 *
 * Rendered as a child route of SiteDashboardLayout, inside the shared
 * dashboard shell (which supplies AppNav + TitleBar). Server-backed via
 * EntityGrid over odl-app/relay-controller-status-history/ scoped to the
 * site's relay controller. On desktop the view splits into a 420 px master
 * list and a side detail panel; on mobile each row expands inline via a
 * DataGrid detail panel. Both paths use the DetailPanel component which
 * fetches the full status record (with measurements) from the legacy endpoint
 * on demand and renders it via StatusDetail.
 *
 * Contains:
 *  - DetailPanel      — per-row detail content (fetches full status from Redux)
 *  - StatusHistoryView — route child component (default export)
 */

import { Box, Paper, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { addMonths } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Map } from 'immutable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import { EntityGrid } from '@sophocles/data-grids';
import {
  fetchRelayControllerStatusHistory,
  relayControllerStatusHistorySelector,
} from '@sophocles/entities/RelayControllerStatusHistory';
import { SMARTLINK_MODEL } from '@sophocles/utils/const';
import { formatDateTime } from '@sophocles/utils/formatUtils';
import { useSiteDashboardLayoutContext } from '@sophocles/views/sites/SiteDashboardLayout';

import StatusDetail from './components/StatusDetail';
import { statusTypeSwitch } from './utils';

// ---------------------------------------------------------------------------
// DetailPanel — fetches and renders the full status record for a history row.
// The list endpoint returns slim rows; the full record (with measurements) is
// loaded on demand from the legacy single-record endpoint via the Redux slice.
// ---------------------------------------------------------------------------

function DetailPanel({ row, unitId, unit, unitTimeZone, isVantage }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const allHistories = useSelector(relayControllerStatusHistorySelector);

  const [loading, setLoading] = useState(false);
  const status = allHistories.get(row?.id?.toString(), Map());

  useEffect(() => {
    if (!row?.id) return;
    if (!status || status.isEmpty()) {
      setLoading(true);
      dispatch(fetchRelayControllerStatusHistory(row.id, isVantage)).finally(() => setLoading(false));
    }
  }, [row?.id]);

  if (!row) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t('relayController.selectStatusEntryPrompt')}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 'var(--radius-md)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <StatusDetail
        isCurrent={false}
        unitId={unitId}
        unit={unit}
        status={status}
        unitTimeZone={unitTimeZone}
        isVantage={isVantage}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// StatusHistoryView
// ---------------------------------------------------------------------------

function StatusHistoryView() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { unit, site, loading } = useSiteDashboardLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const unitId = unit?.id ?? 0;
  const modelId = unit?.model?.id ?? 0;
  const isVantage = modelId === SMARTLINK_MODEL.VANTAGE;
  const unitTimeZone = site?.time_zone ?? 'UTC';

  const [startDate, setStartDate] = useState(() => addMonths(new Date(), -1));
  const [endDate, setEndDate] = useState(() => new Date());
  const [selectedRow, setSelectedRow] = useState(null);

  const handleDateChange = useCallback((start, end) => {
    if (start) setStartDate(start);
    if (end) setEndDate(end);
  }, []);

  // Scope to the relay controller and bound by date range. The new odl-app
  // endpoint normalizes Vantage and standard history into a single resource;
  // receive_time.gte/.lte prunes the underlying partitioned table correctly.
  // (Grid filters use the JSON:API dot-suffix op form, not Django `__`.)
  const extraParams = useMemo(() => {
    const p = {};
    if (unitId) p.relay_controller_id = unitId;
    if (startDate) p['receive_time.gte'] = startDate.toISOString();
    if (endDate) p['receive_time.lte'] = endDate.toISOString();
    return p;
  }, [unitId, startDate, endDate]);

  // Timezone-aware datetime rendering for the primary timestamp column. The
  // descriptor may use receive_time or history_date depending on the device type;
  // override both so either field renders correctly in the site's timezone.
  const fieldOverrides = useMemo(
    () => ({
      receive_time: {
        renderCell: params => {
          if (!params.value) return '';
          try {
            return formatDateTime(toZonedTime(new Date(params.value), unitTimeZone), user);
          } catch {
            return params.value;
          }
        },
      },
      history_date: {
        renderCell: params => {
          if (!params.value) return '';
          try {
            return formatDateTime(toZonedTime(new Date(params.value), unitTimeZone), user);
          } catch {
            return params.value;
          }
        },
      },
      status_type: {
        renderCell: params => statusTypeSwitch(params.value, t),
      },
    }),
    [unitTimeZone, user, t],
  );

  // Desktop row click: select the row so the side detail panel updates.
  const handleRowClick = useCallback(params => setSelectedRow(params.row), []);

  // Mobile: each row expands inline. The detail panel fetches the full record
  // on expand and renders it via StatusDetail (same component as desktop).
  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <Box sx={{ bgcolor: 'var(--surface-secondary)', p: 2 }}>
        <DetailPanel row={row} unitId={unitId} unit={unit} unitTimeZone={unitTimeZone} isVantage={isVantage} />
      </Box>
    ),
    [unitId, unit, unitTimeZone, isVantage],
  );
  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const toolbarContent = useMemo(
    () => (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ),
    [startDate, endDate, handleDateChange],
  );

  if (loading) {
    return <LoadingScreen />;
  }

  if (!unitId) {
    return (
      <Box sx={{ pb: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          {t('relayController.noRelayControllerFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {isMobile ? (
        // Mobile: single list with per-row inline expand/collapse via DataGrid
        // detail panels (passed through EntityGrid's gridProps escape hatch).
        <EntityGrid
          basePath="relay-controller-status-history"
          fieldOverrides={fieldOverrides}
          extraParams={extraParams}
          options={{
            showSearch: false,
            showGridSettings: false,
            infiniteScroll: true,
            toolbarContent,
            gridProps: {
              density: 'compact',
              rowHeight: 40,
              columnHeaderHeight: 38,
              hideFooter: true,
              disableRowSelectionOnClick: true,
              getDetailPanelContent,
              getDetailPanelHeight,
              sx: {
                flex: 1,
                minHeight: 400,
                fontSize: '0.8rem',
                '& .MuiDataGrid-cell': { py: 0.5, display: 'flex', alignItems: 'center' },
                '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', fontWeight: 600 },
                '& .MuiDataGrid-detailPanel': { bgcolor: 'var(--surface-secondary)' },
              },
            },
          }}
        />
      ) : (
        // Desktop: master list on the left (420 px) with a side detail panel.
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: 420, flexShrink: 0 }}>
            <EntityGrid
              basePath="relay-controller-status-history"
              fieldOverrides={fieldOverrides}
              extraParams={extraParams}
              options={{
                showSearch: false,
                showGridSettings: false,
                infiniteScroll: true,
                toolbarContent,
                onRowClick: handleRowClick,
                gridProps: {
                  density: 'compact',
                  rowHeight: 40,
                  columnHeaderHeight: 38,
                  hideFooter: true,
                  disableRowSelectionOnClick: false,
                  sx: {
                    flex: 1,
                    minHeight: 0,
                    fontSize: '0.8rem',
                    '& .MuiDataGrid-cell': { py: 0.5, display: 'flex', alignItems: 'center' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontSize: '0.75rem', fontWeight: 600 },
                    '& .MuiDataGrid-row': {
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'var(--grid-row-hover)' },
                      '&.Mui-selected': {
                        backgroundColor: 'rgb(var(--md-primary-rgb) / 0.12)',
                        '&:hover': { backgroundColor: 'rgb(var(--md-primary-rgb) / 0.16)' },
                      },
                    },
                  },
                },
              }}
            />
          </Box>
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              borderColor: 'var(--grid-border)',
              borderRadius: 'var(--grid-radius)',
              overflow: 'auto',
            }}
          >
            <DetailPanel
              row={selectedRow}
              unitId={unitId}
              unit={unit}
              unitTimeZone={unitTimeZone}
              isVantage={isVantage}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default StatusHistoryView;
